/// <amd-module name="Test/Analytics/Common/Sources/TestReportSource" />
import * as PipelineEntities from "Analytics/Shared/Common/PipelineEntities";
import { AnalyticsODataClientBase } from "Analytics/Shared/Common/Sources/AnalyticsODataClientBase";
import { getDateFilter, parseReturnDateIntoDisplayForm } from "Analytics/Shared/Common/Utils/DateAdjuster";
import { DateSKParser } from "Analytics/Shared/Utils/DateSKParser";
import * as Resources from "Test/Analytics/Common/Resources";
import * as CommonTypes from "Test/Analytics/Common/Types";
import { areReleaseEntitiesRemoved } from "Test/Analytics/Common/Utils/FeatureFlagUtils";
import { StringFormattingUtility } from "Test/Analytics/Common/Utils/StringFormattingUtility";
import { FilterState } from "Test/common-util/Contracts/Filters";
import * as TCMContracts from "Test/common-util/Contracts/Test";
import { FilterValueItem } from "Test/common-util/TestResults.Filtering.Common";

export abstract class TestReportSource extends AnalyticsODataClientBase {
    protected _testRunEntityName: string = "TestRuns";
    protected _testResultEntityName: string = "TestResults";
    protected _testResultDailyEntityName: string = "TestResultsDaily";
    protected _buildPipelineEntityName: string = PipelineEntities.Pipelines;
    protected _releasePipelineEntityName: string = "ReleasePipelines";
    protected _emptyString = "";

    public getOutcomeCountAggregationString(outcomes: CommonTypes.TestOutcome[]): string {
        let outcomeAggrStr = `${this._getOutcomeCountStringForAggregation(
            CommonTypes.TestOutcome.Passed
        )}, ${this._getOutcomeCountStringForAggregation(
            CommonTypes.TestOutcome.NotExecuted
        )}, ${this._getTotalCountStringForAggregation()}, ${this._getOutcomeCountStringForAggregation(CommonTypes.TestOutcome.NotImpacted)}`;

        outcomes.forEach(o => {
            if (o !== CommonTypes.TestOutcome.Passed && o !== CommonTypes.TestOutcome.NotExecuted && o !== CommonTypes.TestOutcome.NotImpacted) {
                outcomeAggrStr += `, ${this._getOutcomeCountStringForAggregation(o)}`;
            }
        });

        return outcomeAggrStr;
    }

    public getOutcomeCountStringForAggregation(outcome: CommonTypes.TestOutcome): string {
        const outcomeCountString = this.getOutcomeCountString(outcome);
        return `${outcomeCountString} with sum as ${outcomeCountString}`;
    }

    /** Converts TestOutcome enum values to the corresponding analytics-backed count field */
    public getOutcomeCountString(outcome: CommonTypes.TestOutcome) {
        switch (outcome) {
            case CommonTypes.TestOutcome.Failed:
                return "ResultFailCount";
            case CommonTypes.TestOutcome.Passed:
                return "ResultPassCount";
            case CommonTypes.TestOutcome.Aborted:
                return "ResultAbortedCount";
            case CommonTypes.TestOutcome.Error:
                return "ResultErrorCount";
            case CommonTypes.TestOutcome.Inconclusive:
                return "ResultInconclusiveCount";
            case CommonTypes.TestOutcome.NotExecuted:
                return "ResultNotExecutedCount";
            case CommonTypes.TestOutcome.NotImpacted:
                return "ResultNotImpactedCount";
            default:
                throw new Error("Unknown TestOutcome.");
        }
    }

    protected _getTestResultsContextFilter(testReportContext: CommonTypes.ITestReportContext): string {
        let queryField = "";
        let queryValue = 0;

        switch (testReportContext.contextType) {
            case TCMContracts.TestResultsContextType.Build:
                queryField = `${PipelineEntities.Pipeline}/${PipelineEntities.PipelineId}`;
                queryValue = testReportContext.definitionId;
                break;

            case TCMContracts.TestResultsContextType.Release:
                queryField = areReleaseEntitiesRemoved(this.pageContext) ? "ReleasePipelineId" : "ReleasePipeline/ReleasePipelineId";
                queryValue = testReportContext.definitionId;
                break;
        }
        return `${queryField} eq ${queryValue}`;
    }

    protected _getDateFilter(trendBy: CommonTypes.TrendBy, period: CommonTypes.Period, datePropertyName?: string): string {
        return getDateFilter(trendBy, period, datePropertyName);
    }

    protected _getFilterByFieldsString(filters?: FilterState): string {
        if (!filters) {
            return "";
        }
        const filterValueList: string[] = [];

        for (const key in filters) {
            if (filters[key] && filters[key].values && filters[key].values.length > 0) {
                const filterValuePredicate: string[] = [];
                filters[key].values.forEach((filterValue: FilterValueItem) => {
                    filterValuePredicate.push(this._getFilterByFieldString(key, filterValue.value.toString()));
                });

                if (filterValuePredicate.length > 0) {
                    filterValueList.push(`(${filterValuePredicate.join(" or ")})`);
                }
            }
        }

        if (filterValueList.length > 0) {
            return ` and (${filterValueList.join(" and ")})`;
        }

        return "";
    }

    protected _getTestRunTitlePropertyString(): string {
        // Derive classes will override implementation of method depending upon table (TestRun or TestResult) use for data source.
        return "";
    }

    protected _getOutcomeCountStringForAggregation(outcome: CommonTypes.TestOutcome): string {
        return this.getOutcomeCountStringForAggregation(outcome);
    }

    protected _getTotalCountStringForAggregation(): string {
        return `ResultCount with sum as ResultCount`;
    }

    protected _getPassPercentage(totalCount: number, passedCount: number, notExecutedCount: number, notImpactedCount: number): number {
        const totalPassedCountIncludingNotImpacted: number = notImpactedCount + passedCount;
        const totalCountExcludingNotExecuted: number = totalCount - notExecutedCount;

        return totalPassedCountIncludingNotImpacted && totalCountExcludingNotExecuted && totalCountExcludingNotExecuted > 0
            ? (totalPassedCountIncludingNotImpacted * 100.0) / totalCountExcludingNotExecuted
            : (0 as number);
    }

    protected _getEntityWiseOutcomeCountParseFunction(entityName: string): Function {
        switch (entityName) {
            case this._testResultEntityName:
                return this._parseOutcomeCountByTestResult;
            case this._testRunEntityName:
            case this._testResultDailyEntityName:
            default:
                return this._parseOutcomeCount;
        }
    }

    protected _parseOutcomeCount(outcome: CommonTypes.TestOutcome, record: any): number {
        switch (outcome) {
            case CommonTypes.TestOutcome.Failed:
                return record.ResultFailCount as number;
            case CommonTypes.TestOutcome.Passed:
                return record.ResultPassCount as number;
            case CommonTypes.TestOutcome.Aborted:
                return record.ResultAbortedCount as number;
            case CommonTypes.TestOutcome.Error:
                return record.ResultErrorCount as number;
            case CommonTypes.TestOutcome.Inconclusive:
                return record.ResultInconclusiveCount as number;
            case CommonTypes.TestOutcome.NotExecuted:
                return record.ResultNotExecutedCount as number;
            case CommonTypes.TestOutcome.NotImpacted:
                return record.ResultNotImpactedCount as number;
        }
        return -1;
    }

    protected _getEntityWiseTotalCountParseFunction(entityName: string): Function {
        switch (entityName) {
            case this._testResultEntityName:
                return this._parseTotalCountByTestResult;
            case this._testRunEntityName:
            case this._testResultDailyEntityName:
            default:
                return this._parseTotalCount;
        }
    }

    protected _getGroupByTestResultsContext(testResultsContext: CommonTypes.ITestReportContext): string {
        switch (testResultsContext.contextType) {
            case TCMContracts.TestResultsContextType.Build:
                return `${PipelineEntities.PipelineRunSK}`;
            case TCMContracts.TestResultsContextType.Release:
                return areReleaseEntitiesRemoved(this.pageContext) ? `ReleaseEnvironmentId` : `ReleaseEnvironmentSK`;
        }
        return "true";
    }

    protected _getGroupByTestPropertiesString(groupBy: CommonTypes.GroupBy | undefined): string {
        switch (groupBy) {
            case CommonTypes.GroupBy.Container:
            case CommonTypes.GroupBy.Owner:
            case CommonTypes.GroupBy.Priority:
            case CommonTypes.GroupBy.Outcome:
            case CommonTypes.GroupBy.Branch:
                return this._getGroupByTestRunPropertiesString(groupBy);
            case CommonTypes.GroupBy.Environment:
                return areReleaseEntitiesRemoved(this.pageContext) ? "ReleaseStageId" : "ReleaseStage/ReleaseStageId";
            case CommonTypes.GroupBy.TestRun:
                return "TestRun/Title";
        }
        return "";
    }

    protected _getGroupByTestRunPropertiesString(groupBy: CommonTypes.GroupBy | undefined): string {
        switch (groupBy) {
            case CommonTypes.GroupBy.Container:
                return "Test/ContainerName";
            case CommonTypes.GroupBy.Owner:
                return "Test/TestOwner";
            case CommonTypes.GroupBy.Priority:
                return "Test/Priority";
            case CommonTypes.GroupBy.Outcome:
                return "Outcome";
            case CommonTypes.GroupBy.Environment:
                return areReleaseEntitiesRemoved(this.pageContext) ? "ReleaseStageId" : "ReleaseStage/ReleaseStageId";
            case CommonTypes.GroupBy.Branch:
                return "Branch/BranchName";
            case CommonTypes.GroupBy.TestRun:
                return "Title";
        }
        return this._emptyString;
    }

    protected _getOutcomeCountCastedAggregationString(outcome: CommonTypes.TestOutcome): string {
        switch (outcome) {
            case CommonTypes.TestOutcome.Failed:
                return `cast(Outcome eq 'Failed',Edm.Int32) with sum as FailedCount`;
            case CommonTypes.TestOutcome.Passed:
                return `cast(Outcome eq 'Passed',Edm.Int32) with sum as PassedCount`;
            case CommonTypes.TestOutcome.Aborted:
                return `cast(Outcome eq 'Aborted',Edm.Int32) with sum as AbortedCount`;
            case CommonTypes.TestOutcome.Error:
                return `cast(Outcome eq 'Error',Edm.Int32) with sum as ErrorCount`;
            case CommonTypes.TestOutcome.Inconclusive:
                return `cast(Outcome eq 'Inconclusive',Edm.Int32) with sum as InconclusiveCount`;
            case CommonTypes.TestOutcome.NotExecuted:
                return `cast(Outcome eq 'NotExecuted',Edm.Int32) with sum as NotExecutedCount`;
            case CommonTypes.TestOutcome.NotImpacted:
                return `cast(Outcome eq 'NotImpacted',Edm.Int32) with sum as NotImpactedCount`;
        }
        return "";
    }

    protected _getTestResultsContextPipelineFilter(testResultsContext: CommonTypes.ITestReportContext): string {
        return this._getTestResultsContextFilter(testResultsContext);
    }

    protected _getDailyDateFilter(trendBy: CommonTypes.TrendBy, period: CommonTypes.Period, dateFieldName?: string): string {
        return getDateFilter(trendBy, period, "DateSK");
    }

    protected _parseTotalCount(record: any): number {
        return record.ResultCount as number;
    }

    protected _getOutcomeCountFilter(outcome: CommonTypes.TestOutcome): string {
        switch (outcome) {
            case CommonTypes.TestOutcome.Failed:
                return `ResultFailCount gt 0`;
            case CommonTypes.TestOutcome.Passed:
                return `ResultPassCount gt 0`;
            case CommonTypes.TestOutcome.Aborted:
                return `ResultAbortedCount gt 0`;
            case CommonTypes.TestOutcome.Error:
                return `ResultErrorCount gt 0`;
            case CommonTypes.TestOutcome.Inconclusive:
                return `ResultInconclusiveCount gt 0`;
            case CommonTypes.TestOutcome.NotExecuted:
                return `ResultNotExecutedCount gt 0`;
            case CommonTypes.TestOutcome.NotImpacted:
                return `ResultNotImpactedCount gt 0`;
        }
        return this._emptyString;
    }

    protected _getFlakyCountAggregationString(): string {
        return `ResultFlakyCount with sum as FlakyCount`;
    }

    protected _getOutcomeFilter(outcome: CommonTypes.TestOutcome): string {
        switch (outcome) {
            case CommonTypes.TestOutcome.Failed:
                return "Outcome eq 'Failed'";
            case CommonTypes.TestOutcome.Passed:
                return "Outcome eq 'Passed'";
            case CommonTypes.TestOutcome.Inconclusive:
                return "Outcome eq 'Inconclusive'";
            case CommonTypes.TestOutcome.Aborted:
                return "Outcome eq 'Aborted'";
            case CommonTypes.TestOutcome.Error:
                return "Outcome eq 'Error'";
            case CommonTypes.TestOutcome.NotExecuted:
                return "Outcome eq 'NotExecuted'";
            case CommonTypes.TestOutcome.NotImpacted:
                return "Outcome eq 'NotImpacted'";
            default:
                return this._emptyString;
        }
    }

    protected _getGroupByPeriodColumnString(trendBy: CommonTypes.TrendBy): string {
        switch (trendBy) {
            case CommonTypes.TrendBy.Days:
                return "CompletedDateSK";
            case CommonTypes.TrendBy.Weeks:
                return "CompletedOn/WeekStartingDate";
        }
    }

    protected _getGroupByDailyPeriodColumnString(trendBy: CommonTypes.TrendBy): string {
        switch (trendBy) {
            case CommonTypes.TrendBy.Days:
                return "DateSK";
            case CommonTypes.TrendBy.Weeks:
                return "Date/WeekStartingDate";
        }
    }

    protected _parseReturnDailyDateIntoDisplayForm(trendBy: CommonTypes.TrendBy, record: any): string {
        switch (trendBy) {
            case CommonTypes.TrendBy.Days:
                return DateSKParser.parseDateSKAsDateString(record.DateSK);
            case CommonTypes.TrendBy.Weeks:
                return DateSKParser.convertDateToDateString(new Date(record.Date.WeekStartingDate)); // Date comes in string for like 'yyyy-mm-ddT00:00:00Z'
        }
    }

    protected _parseReturnDateIntoDisplayForm(trendBy: CommonTypes.TrendBy, record: any): string {
        return parseReturnDateIntoDisplayForm(trendBy, record);
    }

    protected _parseTestProperties(groupBy: CommonTypes.GroupBy | undefined, record: any): { actualValue: string; displayValue: string } {
        switch (groupBy) {
            case CommonTypes.GroupBy.Container:
                return {
                    actualValue: record.Test.ContainerName ? record.Test.ContainerName.toString() : this._emptyString,
                    displayValue: record.Test.ContainerName ? record.Test.ContainerName.toString() : this._emptyString
                };
            case CommonTypes.GroupBy.Owner:
                return {
                    actualValue: record.Test.TestOwner ? record.Test.TestOwner.toString() : this._emptyString,
                    displayValue: record.Test.TestOwner ? record.Test.TestOwner.toString() : Resources.FilterNoOwner
                };
            case CommonTypes.GroupBy.Priority:
                // Converting number to string here. We should see if we convert to number then in detailed list grid order of data displayed will not change.
                return {
                    actualValue: record.Test.Priority != null ? record.Test.Priority.toString() : 255,
                    displayValue:
                        record.Test.Priority != null
                            ? (record.Test.Priority as number) !== 255
                                ? record.Test.Priority.toString()
                                : Resources.NotAvailable
                            : Resources.NotAvailable
                };
            case CommonTypes.GroupBy.Outcome:
                return {
                    actualValue: record.Outcome ? record.Outcome.toString() : this._emptyString,
                    displayValue: record.Outcome ? record.Outcome.toString() : this._emptyString
                };
            case CommonTypes.GroupBy.Environment:
                // For TestResultsDaily and TestResults
                let releaseStageIdString = this._emptyString;
                let releaseEnvDefIdString = this._emptyString;
                if (areReleaseEntitiesRemoved(this.pageContext)) {
                    releaseStageIdString = record.ReleaseStageId ? record.ReleaseStageId.toString() : this._emptyString;
                } else {
                    releaseStageIdString =
                        record.ReleaseStage && record.ReleaseStage.ReleaseStageId ? record.ReleaseStage.ReleaseStageId.toString() : this._emptyString;

                    releaseEnvDefIdString =
                        record.ReleaseEnvironment && record.ReleaseEnvironment.ReleaseEnvironmentDefinitionId
                            ? record.ReleaseEnvironment.ReleaseEnvironmentDefinitionId.toString()
                            : this._emptyString;
                }

                // For TestRun

                return {
                    actualValue: releaseStageIdString || releaseEnvDefIdString,
                    displayValue: releaseStageIdString || releaseEnvDefIdString
                };
            case CommonTypes.GroupBy.Branch:
                return {
                    actualValue: record.Branch.BranchName ? record.Branch.BranchName.toString() : this._emptyString,
                    displayValue: record.Branch.BranchName ? record.Branch.BranchName.toString() : Resources.NoBranchText
                };
            case CommonTypes.GroupBy.TestRun:
                return {
                    actualValue: record.Title
                        ? record.Title.toString()
                        : record.TestRun && record.TestRun.Title
                        ? record.TestRun.Title.toString()
                        : this._emptyString,
                    displayValue: record.Title
                        ? record.Title.toString()
                        : record.TestRun && record.TestRun.Title
                        ? record.TestRun.Title.toString()
                        : this._emptyString
                };
            default:
                return {
                    actualValue: this._emptyString,
                    displayValue: this._emptyString
                };
        }
    }

    protected _getOutcomeCountAggregateAndComputeStrings(
        outcomes: CommonTypes.TestOutcome[]
    ): { outcomeAggregationStr: string; outcomeComputeStr: string } {
        let outcomeAggregationStr: string = this._emptyString;
        let outcomeComputeStr: string = this._emptyString;

        if (outcomes.length > 0) {
            outcomes.forEach(o => {
                if (!outcomeAggregationStr) {
                    outcomeAggregationStr = this._getOutcomeCountStringForAggregation(o);
                    outcomeComputeStr = this._getOutcomeComputeStrings(o);
                } else {
                    outcomeAggregationStr += `, ${this._getOutcomeCountStringForAggregation(o)}`;
                    outcomeComputeStr += ` add ${this._getOutcomeComputeStrings(o)}`;
                }
            });
        } else {
            // If no filters specified, AggregateValue is positioned inside the aggregate clause
            outcomeAggregationStr = "ResultCount with sum as AggregateValue";
        }

        return { outcomeAggregationStr: outcomeAggregationStr, outcomeComputeStr: outcomeComputeStr };
    }

    protected _getOutcomeComputeStrings(outcome: CommonTypes.TestOutcome): string {
        switch (outcome) {
            case CommonTypes.TestOutcome.Failed:
                return `ResultFailCount`;
            case CommonTypes.TestOutcome.Passed:
                return `ResultPassCount`;
            case CommonTypes.TestOutcome.Aborted:
                return `ResultAbortedCount`;
            case CommonTypes.TestOutcome.Error:
                return `ResultErrorCount`;
            case CommonTypes.TestOutcome.Inconclusive:
                return `ResultInconclusiveCount`;
            case CommonTypes.TestOutcome.NotExecuted:
                return `ResultNotExecutedCount`;
            case CommonTypes.TestOutcome.NotImpacted:
                return `ResultNotImpactedCount`;
            default:
                return this._emptyString;
        }
    }

    private _getFilterByFieldString(filterField: string, filterSelectedValue: string): string {
        let filterFormattedValue: string = StringFormattingUtility.getFormattedValueStringForODataQuery(filterSelectedValue.toString());

        switch (filterField) {
            case CommonTypes.Filter.Workflow:
                filterFormattedValue = CommonTypes.Workflow[Number(filterFormattedValue)];
                break;
            case CommonTypes.Filter.Outcome:
                filterFormattedValue = CommonTypes.TestOutcome[Number(filterFormattedValue)];
                break;
            case CommonTypes.Filter.Branch:
            case CommonTypes.Filter.Environment:
                return `${this._getFilterPropertiesString(filterField)} eq ${filterFormattedValue}`;
        }

        return `${this._getFilterPropertiesString(filterField)} eq '${filterFormattedValue}'`;
    }

    private _getFilterPropertiesString(filterField: string): string {
        switch (filterField) {
            case CommonTypes.Filter.Workflow:
                return "Workflow";
            case CommonTypes.Filter.Outcome:
                return "Outcome";
            case CommonTypes.Filter.Container:
                return "Test/ContainerName";
            case CommonTypes.Filter.TestRun:
                return this._getTestRunTitlePropertyString();
            case CommonTypes.Filter.Owner:
                return "Test/TestOwner";
            case CommonTypes.Filter.Branch:
                return "BranchSK";
            case CommonTypes.Filter.Environment:
                return areReleaseEntitiesRemoved(this.pageContext) ? "ReleaseStageId" : "ReleaseStageSK";
            default:
                return "";
        }
    }

    private _parseOutcomeCountByTestResult(outcome: CommonTypes.TestOutcome, record: any): number {
        switch (outcome) {
            case CommonTypes.TestOutcome.Failed:
                return record.FailedCount as number;
            case CommonTypes.TestOutcome.Passed:
                return record.PassedCount as number;
            case CommonTypes.TestOutcome.Aborted:
                return record.AbortedCount as number;
            case CommonTypes.TestOutcome.Error:
                return record.ErrorCount as number;
            case CommonTypes.TestOutcome.Inconclusive:
                return record.InconclusiveCount as number;
            case CommonTypes.TestOutcome.NotExecuted:
                return record.NotExecutedCount as number;
            case CommonTypes.TestOutcome.NotImpacted:
                return record.NotImpactedCount as number;
        }
        return -1;
    }

    private _parseTotalCountByTestResult(record: any): number {
        return record.TotalCount as number;
    }
}
